import { supabase } from '../lib/supabaseClient';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://xtllnsjfowxdpymrnpjg.supabase.co';

/**
 * Get the base URL for the application
 * Falls back to production URL if window is not available
 */
function getBaseUrl(): string {
  // Try to get from environment variable first
  if (process.env.REACT_APP_BASE_URL) {
    return process.env.REACT_APP_BASE_URL;
  }

  // Try to get from window.location if available
  if (typeof window !== 'undefined' && window.location) {
    return window.location.origin;
  }

  // Fallback to production URL
  return 'https://til.scr.vic.edu.au';
}

interface SendEmailParams {
  to: string | string[];
  subject: string;
  html: string;
  templateType: string;
  requestId?: string;
  notificationId?: string;
  userId?: string;
  metadata?: Record<string, any>;
}

class SupabaseEmailService {
  /**
   * Send an email via Supabase Edge Function
   */
  async sendEmail(params: SendEmailParams): Promise<{ success: boolean; emailId?: string; error?: string }> {
    const isDev = process.env.NODE_ENV === 'development';
    try {
      if (isDev) {
        console.log('[EMAIL SERVICE] sendEmail called', {
          templateType: params.templateType,
          requestId: params.requestId,
        });
      }

      const { data: functionData, error: functionError } = await supabase.functions.invoke('send-email', {
        body: params,
      });

      if (functionError) {
        if (isDev) console.error('[EMAIL SERVICE] Edge function error:', functionError.message);
        return { success: false, error: functionError.message };
      }

      if (functionData && functionData.success) {
        if (isDev) console.log('[EMAIL SERVICE] Email sent');
        return { success: true, emailId: functionData.emailId };
      }

      return { success: false, error: 'Unknown error sending email' };
    } catch (error: any) {
      if (isDev) console.error('[EMAIL SERVICE] Exception sending email:', error?.message);
      return { success: false, error: error.message };
    }
  }

  /**
   * Notify department leader about new Extra Hours request
   */
  async notifyLeaderExtraHoursSubmitted(params: {
    leaderId: string;
    leaderName: string;
    leaderEmail: string;
    staffName: string;
    staffEmployeeId: string;
    departmentName: string;
    requestId: string;
    requestDate: string;
    workDate: string;
    startTime: string;
    endTime: string;
    totalHours: number;
    reason: string;
  }): Promise<{ success: boolean; error?: string }> {
    const approvalUrl = `${getBaseUrl()}/leader/approval?requestId=${params.requestId}`;

    const html = this.renderExtraHoursSubmitted({
      leaderName: params.leaderName,
      staffName: params.staffName,
      staffEmployeeId: params.staffEmployeeId,
      departmentName: params.departmentName,
      requestDate: params.requestDate,
      workDate: params.workDate,
      startTime: params.startTime,
      endTime: params.endTime,
      totalHours: params.totalHours,
      reason: params.reason,
      approvalUrl,
    });

    return this.sendEmail({
      to: params.leaderEmail,
      subject: `New Extra Hours Request from ${params.staffName}`,
      html,
      templateType: 'extra_hours_submitted',
      requestId: params.requestId,
      userId: params.leaderId,
      metadata: { staffName: params.staffName, totalHours: params.totalHours },
    });
  }

  /**
   * Notify organisers about new TIL request
   */
  async notifyOrganisersTILRequestSubmitted(params: {
    organisers: Array<{ id: string; full_name: string; email: string }>;
    staffName: string;
    staffEmployeeId: string;
    departmentName: string;
    requestId: string;
    requestDate: string;
    tilDates: string;
    timeRange: string;
    totalHours: number;
    currentBalance: number;
    balanceAfterApproval: number;
    reason: string;
  }): Promise<{ success: boolean; error?: string }> {
    const approvalUrl = `${getBaseUrl()}/organiser/approval?requestId=${params.requestId}`;

    const results = await Promise.all(
      params.organisers.map((organiser) => {
        const html = this.renderTILRequestSubmitted({
          organiserName: organiser.full_name,
          staffName: params.staffName,
          staffEmployeeId: params.staffEmployeeId,
          departmentName: params.departmentName,
          requestDate: params.requestDate,
          tilDates: params.tilDates,
          timeRange: params.timeRange,
          totalHours: params.totalHours,
          currentBalance: params.currentBalance,
          balanceAfterApproval: params.balanceAfterApproval,
          reason: params.reason,
          approvalUrl,
        });

        return this.sendEmail({
          to: organiser.email,
          subject: `New TIL Request from ${params.staffName}`,
          html,
          templateType: 'til_request_submitted',
          requestId: params.requestId,
          userId: organiser.id,
          metadata: { staffName: params.staffName, totalHours: params.totalHours },
        });
      })
    );

    const allSuccessful = results.every((r) => r.success);
    return {
      success: allSuccessful,
      error: allSuccessful ? undefined : 'Some emails failed to send',
    };
  }

  /**
   * Notify requester that their Extra Hours request was approved
   */
  async notifyRequesterExtraHoursApproved(params: {
    requesterId: string;
    requesterEmail: string;
    staffName: string;
    approverName: string;
    requestId: string;
    workDate: string;
    startTime: string;
    endTime: string;
    totalHours: number;
    approvedDate: string;
    newTILBalance: number;
    comments?: string;
    notificationId?: string;
  }): Promise<{ success: boolean; error?: string }> {
    const viewRequestUrl = `${getBaseUrl()}/requests`;

    const html = this.renderExtraHoursApproved({
      staffName: params.staffName,
      approverName: params.approverName,
      workDate: params.workDate,
      startTime: params.startTime,
      endTime: params.endTime,
      totalHours: params.totalHours,
      approvedDate: params.approvedDate,
      newTILBalance: params.newTILBalance,
      comments: params.comments,
      viewRequestUrl,
    });

    return this.sendEmail({
      to: params.requesterEmail,
      subject: 'Your Extra Hours Request Has Been Approved',
      html,
      templateType: 'extra_hours_approved',
      requestId: params.requestId,
      notificationId: params.notificationId,
      userId: params.requesterId,
      metadata: { approverName: params.approverName, totalHours: params.totalHours },
    });
  }

  /**
   * Notify requester that their Extra Hours request was rejected
   */
  async notifyRequesterExtraHoursRejected(params: {
    requesterId: string;
    requesterEmail: string;
    staffName: string;
    approverName: string;
    requestId: string;
    workDate: string;
    startTime: string;
    endTime: string;
    totalHours: number;
    rejectedDate: string;
    rejectionReason: string;
    notificationId?: string;
  }): Promise<{ success: boolean; error?: string }> {
    const viewRequestUrl = `${getBaseUrl()}/requests`;

    const html = this.renderExtraHoursRejected({
      staffName: params.staffName,
      approverName: params.approverName,
      workDate: params.workDate,
      startTime: params.startTime,
      endTime: params.endTime,
      totalHours: params.totalHours,
      rejectedDate: params.rejectedDate,
      rejectionReason: params.rejectionReason,
      viewRequestUrl,
    });

    return this.sendEmail({
      to: params.requesterEmail,
      subject: 'Your Extra Hours Request Was Not Approved',
      html,
      templateType: 'extra_hours_rejected',
      requestId: params.requestId,
      notificationId: params.notificationId,
      userId: params.requesterId,
      metadata: { approverName: params.approverName, reason: params.rejectionReason },
    });
  }

  /**
   * Notify requester that their TIL request was approved
   */
  async notifyRequesterTILRequestApproved(params: {
    requesterId: string;
    requesterEmail: string;
    staffName: string;
    approverName: string;
    requestId: string;
    tilDates: string;
    timeRange: string;
    totalHours: number;
    approvedDate: string;
    newTILBalance: number;
    comments?: string;
    notificationId?: string;
  }): Promise<{ success: boolean; error?: string }> {
    const viewRequestUrl = `${getBaseUrl()}/requests`;

    const html = this.renderTILRequestApproved({
      staffName: params.staffName,
      approverName: params.approverName,
      tilDates: params.tilDates,
      timeRange: params.timeRange,
      totalHours: params.totalHours,
      approvedDate: params.approvedDate,
      newTILBalance: params.newTILBalance,
      comments: params.comments,
      viewRequestUrl,
    });

    return this.sendEmail({
      to: params.requesterEmail,
      subject: 'Your TIL Request Has Been Approved',
      html,
      templateType: 'til_request_approved',
      requestId: params.requestId,
      notificationId: params.notificationId,
      userId: params.requesterId,
      metadata: { approverName: params.approverName, totalHours: params.totalHours },
    });
  }

  /**
   * Notify department leader that one of their staff's TIL request was approved
   */
  async notifyLeaderTILApproved(params: {
    leaderId: string;
    leaderName: string;
    leaderEmail: string;
    staffName: string;
    staffEmployeeId: string;
    departmentName: string;
    requestId: string;
    tilDates: string;
    timeRange: string;
    totalHours: number;
    approvedDate: string;
    approverName: string;
    reason?: string;
  }): Promise<{ success: boolean; error?: string }> {
    const html = this.renderLeaderTILApproved({
      leaderName: params.leaderName,
      staffName: params.staffName,
      staffEmployeeId: params.staffEmployeeId,
      departmentName: params.departmentName,
      tilDates: params.tilDates,
      timeRange: params.timeRange,
      totalHours: params.totalHours,
      approvedDate: params.approvedDate,
      approverName: params.approverName,
      reason: params.reason,
    });

    return this.sendEmail({
      to: params.leaderEmail,
      subject: `TIL Approved: ${params.staffName} - ${params.tilDates}`,
      html,
      templateType: 'leader_til_notification',
      requestId: params.requestId,
      userId: params.leaderId,
      metadata: { staffName: params.staffName, totalHours: params.totalHours },
    });
  }

  /**
   * Notify requester that their TIL request was rejected
   */
  async notifyRequesterTILRequestRejected(params: {
    requesterId: string;
    requesterEmail: string;
    staffName: string;
    approverName: string;
    requestId: string;
    tilDates: string;
    timeRange: string;
    totalHours: number;
    rejectedDate: string;
    rejectionReason: string;
    currentBalance: number;
    notificationId?: string;
  }): Promise<{ success: boolean; error?: string }> {
    const viewRequestUrl = `${getBaseUrl()}/requests`;

    const html = this.renderTILRequestRejected({
      staffName: params.staffName,
      approverName: params.approverName,
      tilDates: params.tilDates,
      timeRange: params.timeRange,
      totalHours: params.totalHours,
      rejectedDate: params.rejectedDate,
      rejectionReason: params.rejectionReason,
      currentBalance: params.currentBalance,
      viewRequestUrl,
    });

    return this.sendEmail({
      to: params.requesterEmail,
      subject: 'Your TIL Request Was Not Approved',
      html,
      templateType: 'til_request_rejected',
      requestId: params.requestId,
      notificationId: params.notificationId,
      userId: params.requesterId,
      metadata: { approverName: params.approverName, reason: params.rejectionReason },
    });
  }

  /**
   * Send receipt to requester when they submit an Extra Hours request
   */
  async notifyRequesterExtraHoursReceived(params: {
    requesterId: string;
    requesterEmail: string;
    staffName: string;
    workDate: string;
    startTime: string;
    endTime: string;
    totalHours: number;
    reason: string;
    departmentName: string;
    leaderName: string;
    requestId: string;
    submittedDate: string;
  }): Promise<{ success: boolean; error?: string }> {
    const viewRequestUrl = `${getBaseUrl()}/requests`;

    const html = this.renderExtraHoursRequestReceipt({
      staffName: params.staffName,
      workDate: params.workDate,
      startTime: params.startTime,
      endTime: params.endTime,
      totalHours: params.totalHours,
      reason: params.reason,
      departmentName: params.departmentName,
      leaderName: params.leaderName,
      submittedDate: params.submittedDate,
      viewRequestUrl,
    });

    return this.sendEmail({
      to: params.requesterEmail,
      subject: 'Extra Hours Request Submitted Successfully',
      html,
      templateType: 'extra_hours_receipt',
      requestId: params.requestId,
      userId: params.requesterId,
      metadata: { totalHours: params.totalHours, departmentName: params.departmentName },
    });
  }

  /**
   * Send receipt to requester when they submit a TIL request
   */
  async notifyRequesterTILRequestReceived(params: {
    requesterId: string;
    requesterEmail: string;
    staffName: string;
    tilDates: string;
    timeRange: string;
    totalHours: number;
    reason: string;
    currentBalance: number;
    balanceAfterApproval: number;
    requestId: string;
    submittedDate: string;
  }): Promise<{ success: boolean; error?: string }> {
    const viewRequestUrl = `${getBaseUrl()}/requests`;

    const html = this.renderTILRequestReceipt({
      staffName: params.staffName,
      tilDates: params.tilDates,
      timeRange: params.timeRange,
      totalHours: params.totalHours,
      reason: params.reason,
      currentBalance: params.currentBalance,
      balanceAfterApproval: params.balanceAfterApproval,
      submittedDate: params.submittedDate,
      viewRequestUrl,
    });

    return this.sendEmail({
      to: params.requesterEmail,
      subject: 'TIL Request Submitted Successfully',
      html,
      templateType: 'til_request_receipt',
      requestId: params.requestId,
      userId: params.requesterId,
      metadata: { totalHours: params.totalHours, currentBalance: params.currentBalance },
    });
  }

  // HTML Template Renderers - Copied from Edge Function templates
  // These generate inline HTML for the emails

  private renderExtraHoursSubmitted(data: any): string {
    // Simple inline template - in production, you might want to use a templating library
    return `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f6f9fc;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">
    <div style="padding: 32px 40px; background-color: #1976d2; color: #ffffff;">
      <h1 style="color: #ffffff; font-size: 28px; margin: 0 0 8px;">New Extra Hours Request</h1>
      <p style="color: #e3f2fd; font-size: 14px; margin: 0;">TIL Management System - Salesian College Sunbury</p>
    </div>
    <div style="padding: 32px 40px;">
      <p style="font-size: 16px; line-height: 1.6; margin: 16px 0;">Hi <strong>${data.leaderName}</strong>,</p>
      <p style="font-size: 16px; line-height: 1.6; margin: 16px 0;">A member of your department has submitted an extra hours request that requires your approval.</p>
      <hr style="border: none; border-top: 1px solid #e0e0e0; margin: 24px 0;">
      <div style="background-color: #f5f5f5; border-left: 4px solid #1976d2; border-radius: 4px; padding: 16px 20px; margin: 16px 0;">
        <p style="font-size: 14px; font-weight: bold; margin: 0 0 12px;"><strong>${data.staffName}</strong> - ${data.departmentName}</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Date:</strong> ${data.workDate}</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Time:</strong> ${data.startTime} - ${data.endTime}</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Hours:</strong> <span style="color: #1976d2; font-weight: bold;">${data.totalHours} hours</span></p>
        ${data.reason ? `<p style="font-size: 14px; margin: 12px 0 0; font-style: italic;">${data.reason}</p>` : ''}
      </div>
      <div style="text-align: center; margin: 24px 0;">
        <a href="${data.approvalUrl}" style="display: inline-block; background-color: #1976d2; color: #ffffff; font-size: 16px; font-weight: bold; text-decoration: none; padding: 12px 32px; border-radius: 4px;">Review & Approve Request</a>
      </div>
    </div>
    <div style="padding: 0 40px 32px; border-top: 1px solid #e0e0e0;">
      <p style="color: #666666; font-size: 12px; margin: 8px 0;">This is an automated message from the TIL Management System.</p>
    </div>
  </div>
</body>
</html>
    `;
  }

  private renderTILRequestSubmitted(data: any): string {
    return `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f6f9fc;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">
    <div style="padding: 32px 40px; background-color: #1976d2; color: #ffffff;">
      <h1 style="color: #ffffff; font-size: 28px; margin: 0 0 8px;">New TIL Request</h1>
      <p style="color: #e3f2fd; font-size: 14px; margin: 0;">TIL Management System - Salesian College Sunbury</p>
    </div>
    <div style="padding: 32px 40px;">
      <p style="font-size: 16px; line-height: 1.6; margin: 16px 0;">Hi <strong>${data.organiserName}</strong>,</p>
      <p style="font-size: 16px; line-height: 1.6; margin: 16px 0;">A staff member has submitted a Time In Lieu (TIL) request that requires your approval.</p>
      <hr style="border: none; border-top: 1px solid #e0e0e0; margin: 24px 0;">
      <div style="background-color: #f5f5f5; border-left: 4px solid #1976d2; border-radius: 4px; padding: 16px 20px; margin: 16px 0;">
        <p style="font-size: 14px; font-weight: bold; margin: 0 0 12px;"><strong>${data.staffName}</strong> - ${data.departmentName}</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Dates:</strong> ${data.tilDates}</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Time:</strong> ${data.timeRange}</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Hours:</strong> <span style="color: #1976d2; font-weight: bold;">${data.totalHours} hours</span></p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Balance After:</strong> ${data.balanceAfterApproval} hours</p>
        ${data.reason ? `<p style="font-size: 14px; margin: 12px 0 0; font-style: italic;">${data.reason}</p>` : ''}
      </div>
      <div style="text-align: center; margin: 24px 0;">
        <a href="${data.approvalUrl}" style="display: inline-block; background-color: #1976d2; color: #ffffff; font-size: 16px; font-weight: bold; text-decoration: none; padding: 12px 32px; border-radius: 4px;">Review & Approve Request</a>
      </div>
    </div>
    <div style="padding: 0 40px 32px; border-top: 1px solid #e0e0e0;">
      <p style="color: #666666; font-size: 12px; margin: 8px 0;">This is an automated message from the TIL Management System.</p>
    </div>
  </div>
</body>
</html>
    `;
  }

  private renderExtraHoursApproved(data: any): string {
    return `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f6f9fc;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">
    <div style="padding: 32px 40px; background-color: #1976d2; color: #ffffff;">
      <h1 style="color: #ffffff; font-size: 28px; margin: 0 0 8px;">Request Approved</h1>
      <p style="color: #e3f2fd; font-size: 14px; margin: 0;">TIL Management System</p>
    </div>
    <div style="padding: 32px 40px;">
      <p>Hi <strong>${data.staffName}</strong>,</p>
      <div style="background-color: #e8f5e9; border-left: 4px solid #4caf50; padding: 16px 20px; margin: 16px 0; text-align: center;">
        <p style="color: #2e7d32; font-size: 16px; margin: 0;">✓ Your extra hours request has been <strong>approved</strong>!</p>
      </div>
      <p>Your new TIL balance is <strong>${data.newTILBalance} hours</strong>.</p>
      <div style="text-align: center; margin: 24px 0;">
        <a href="${data.viewRequestUrl}" style="display: inline-block; background-color: #1976d2; color: #ffffff; font-size: 16px; font-weight: bold; text-decoration: none; padding: 12px 32px; border-radius: 4px;">View Details</a>
      </div>
    </div>
  </div>
</body>
</html>
    `;
  }

  private renderExtraHoursRejected(data: any): string {
    return `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f6f9fc;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">
    <div style="padding: 32px 40px; background-color: #1976d2; color: #ffffff;">
      <h1 style="color: #ffffff; font-size: 28px; margin: 0 0 8px;">Request Not Approved</h1>
      <p style="color: #e3f2fd; font-size: 14px; margin: 0;">TIL Management System</p>
    </div>
    <div style="padding: 32px 40px;">
      <p>Hi <strong>${data.staffName}</strong>,</p>
      <p>Your extra hours request was not approved.</p>
      <div style="background-color: #fff3e0; border-left: 4px solid #ff9800; padding: 16px 20px; margin: 16px 0;">
        <p style="font-size: 14px; margin: 0;"><strong>Reason:</strong> ${data.rejectionReason}</p>
      </div>
      <p>Please contact ${data.approverName} if you have questions.</p>
    </div>
  </div>
</body>
</html>
    `;
  }

  private renderTILRequestApproved(data: any): string {
    return `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f6f9fc;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">
    <div style="padding: 32px 40px; background-color: #1976d2; color: #ffffff;">
      <h1 style="color: #ffffff; font-size: 28px; margin: 0 0 8px;">TIL Request Approved</h1>
      <p style="color: #e3f2fd; font-size: 14px; margin: 0;">TIL Management System</p>
    </div>
    <div style="padding: 32px 40px;">
      <p>Hi <strong>${data.staffName}</strong>,</p>
      <div style="background-color: #e8f5e9; border-left: 4px solid #4caf50; padding: 16px 20px; margin: 16px 0; text-align: center;">
        <p style="color: #2e7d32; font-size: 16px; margin: 0;">✓ Your TIL request has been <strong>approved</strong>!</p>
      </div>
      <p>Your new TIL balance is <strong>${data.newTILBalance} hours</strong>.</p>
      <p><strong>Approved dates:</strong> ${data.tilDates}</p>
      <div style="text-align: center; margin: 24px 0;">
        <a href="${data.viewRequestUrl}" style="display: inline-block; background-color: #1976d2; color: #ffffff; font-size: 16px; font-weight: bold; text-decoration: none; padding: 12px 32px; border-radius: 4px;">View Details</a>
      </div>
    </div>
  </div>
</body>
</html>
    `;
  }

  private renderTILRequestRejected(data: any): string {
    return `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f6f9fc;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">
    <div style="padding: 32px 40px; background-color: #1976d2; color: #ffffff;">
      <h1 style="color: #ffffff; font-size: 28px; margin: 0 0 8px;">TIL Request Not Approved</h1>
      <p style="color: #e3f2fd; font-size: 14px; margin: 0;">TIL Management System</p>
    </div>
    <div style="padding: 32px 40px;">
      <p>Hi <strong>${data.staffName}</strong>,</p>
      <p>Your TIL request was not approved. Your balance remains unchanged at <strong>${data.currentBalance} hours</strong>.</p>
      <div style="background-color: #fff3e0; border-left: 4px solid #ff9800; padding: 16px 20px; margin: 16px 0;">
        <p style="font-size: 14px; margin: 0;"><strong>Reason:</strong> ${data.rejectionReason}</p>
      </div>
      <p>Please contact ${data.approverName} if you have questions.</p>
    </div>
  </div>
</body>
</html>
    `;
  }

  private renderLeaderTILApproved(data: any): string {
    return `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f6f9fc;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">
    <div style="padding: 32px 40px; background-color: #1976d2; color: #ffffff;">
      <h1 style="color: #ffffff; font-size: 28px; margin: 0 0 8px;">Staff TIL Notification</h1>
      <p style="color: #e3f2fd; font-size: 14px; margin: 0;">TIL Management System</p>
    </div>
    <div style="padding: 32px 40px;">
      <p>Hi <strong>${data.leaderName}</strong>,</p>
      <div style="background-color: #e3f2fd; border-left: 4px solid #1976d2; padding: 16px 20px; margin: 16px 0;">
        <p style="color: #1565c0; font-size: 16px; margin: 0;">
          <strong>${data.staffName}</strong> (${data.staffEmployeeId}) from your department has been approved to take Time in Lieu.
        </p>
      </div>

      <hr style="border: none; border-top: 1px solid #e0e0e0; margin: 24px 0;">

      <div style="background-color: #f5f5f5; border-left: 4px solid #1976d2; border-radius: 4px; padding: 16px 20px; margin: 16px 0;">
        <p style="font-size: 14px; font-weight: bold; margin: 0 0 12px;">TIL Details</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Staff Member:</strong> ${data.staffName} (${data.staffEmployeeId})</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Department:</strong> ${data.departmentName}</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Dates:</strong> ${data.tilDates}</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Time:</strong> ${data.timeRange}</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Total Hours:</strong> <span style="color: #1976d2; font-weight: bold;">${data.totalHours} hours</span></p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Approved By:</strong> ${data.approverName}</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Approved Date:</strong> ${data.approvedDate}</p>
        ${data.reason ? `<p style="font-size: 14px; margin: 12px 0 0;"><strong>Reason:</strong> <span style="font-style: italic;">${data.reason}</span></p>` : ''}
      </div>

      <div style="background-color: #fff3e0; border-left: 4px solid #ff9800; padding: 16px 20px; margin: 24px 0;">
        <p style="font-size: 13px; margin: 0; color: #e65100;">
          <strong>Note:</strong> This is a notification only. The TIL has already been approved by ${data.approverName}.
          Please ensure appropriate coverage is arranged for ${data.staffName} during their absence.
        </p>
      </div>

      <p style="font-size: 13px; color: #666; margin: 24px 0 0;">
        If you have any questions about this TIL request, please contact ${data.approverName} or the staff member directly.
      </p>
    </div>
    <div style="padding: 24px 40px; background-color: #f6f9fc; text-align: center; border-top: 1px solid #e0e0e0;">
      <p style="font-size: 12px; color: #999; margin: 0;">
        TIL Management System - Salesian College Sunbury
      </p>
    </div>
  </div>
</body>
</html>
    `;
  }

  private renderExtraHoursRequestReceipt(data: any): string {
    return `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f6f9fc;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">
    <div style="padding: 32px 40px; background-color: #1976d2; color: #ffffff;">
      <h1 style="color: #ffffff; font-size: 28px; margin: 0 0 8px;">Request Submitted Successfully</h1>
      <p style="color: #e3f2fd; font-size: 14px; margin: 0;">TIL Management System</p>
    </div>
    <div style="padding: 32px 40px;">
      <p>Hi <strong>${data.staffName}</strong>,</p>
      <div style="background-color: #e8f5e9; border-left: 4px solid #4caf50; padding: 16px 20px; margin: 16px 0; text-align: center;">
        <p style="color: #2e7d32; font-size: 16px; margin: 0;">✓ Your extra hours request has been <strong>submitted successfully</strong>!</p>
      </div>
      <p>Your request has been sent to <strong>${data.leaderName}</strong> (${data.departmentName}) for review. You'll receive another email once your request has been reviewed.</p>
      <hr style="border: none; border-top: 1px solid #e0e0e0; margin: 24px 0;">
      <div style="background-color: #f5f5f5; border-left: 4px solid #1976d2; border-radius: 4px; padding: 16px 20px; margin: 16px 0;">
        <p style="font-size: 14px; font-weight: bold; margin: 0 0 12px;">Request Details</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Date Worked:</strong> ${data.workDate}</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Time:</strong> ${data.startTime} - ${data.endTime}</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Hours:</strong> <span style="color: #1976d2; font-weight: bold;">${data.totalHours} hours</span></p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Submitted:</strong> ${data.submittedDate}</p>
        ${data.reason ? `<p style="font-size: 14px; margin: 12px 0 0; font-style: italic;">${data.reason}</p>` : ''}
      </div>
      <div style="background-color: #e8f5e9; padding: 12px; border-radius: 4px; margin: 16px 0;">
        <p style="font-size: 14px; margin: 4px 0;">• Your department leader will review your request</p>
        <p style="font-size: 14px; margin: 4px 0;">• If approved, ${data.totalHours} hours will be added to your TIL balance</p>
        <p style="font-size: 14px; margin: 4px 0;">• You'll be notified by email when a decision is made</p>
      </div>
    </div>
  </div>
</body>
</html>
    `;
  }

  private renderTILRequestReceipt(data: any): string {
    const sufficientBalance = data.balanceAfterApproval >= 0;
    return `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f6f9fc;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">
    <div style="padding: 32px 40px; background-color: #1976d2; color: #ffffff;">
      <h1 style="color: #ffffff; font-size: 28px; margin: 0 0 8px;">TIL Request Submitted</h1>
      <p style="color: #e3f2fd; font-size: 14px; margin: 0;">TIL Management System</p>
    </div>
    <div style="padding: 32px 40px;">
      <p>Hi <strong>${data.staffName}</strong>,</p>
      <div style="background-color: #e8f5e9; border-left: 4px solid #4caf50; padding: 16px 20px; margin: 16px 0; text-align: center;">
        <p style="color: #2e7d32; font-size: 16px; margin: 0;">✓ Your Time In Lieu request has been <strong>submitted successfully</strong>!</p>
      </div>
      <p>Your request has been sent to the TIL organisers for review. You'll receive another email once your request has been reviewed.</p>
      <hr style="border: none; border-top: 1px solid #e0e0e0; margin: 24px 0;">
      <div style="background-color: #f5f5f5; border-left: 4px solid #1976d2; border-radius: 4px; padding: 16px 20px; margin: 16px 0;">
        <p style="font-size: 14px; font-weight: bold; margin: 0 0 12px;">Request Details</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Dates:</strong> ${data.tilDates}</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Time:</strong> ${data.timeRange}</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Hours:</strong> <span style="color: #1976d2; font-weight: bold;">${data.totalHours} hours</span></p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Submitted:</strong> ${data.submittedDate}</p>
      </div>
      <div style="background-color: #f5f5f5; border-left: 4px solid #1976d2; border-radius: 4px; padding: 16px 20px; margin: 16px 0;">
        <p style="font-size: 14px; font-weight: bold; margin: 0 0 12px;">Your TIL Balance</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Current Balance:</strong> ${data.currentBalance} hours</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Balance If Approved:</strong> <span style="color: #1976d2; font-weight: bold;">${data.balanceAfterApproval} hours</span></p>
        ${!sufficientBalance ? `<p style="font-size: 13px; color: #e65100; font-weight: bold; margin: 8px 0 0;">⚠️ Note: This request exceeds your available balance</p>` : ''}
      </div>
      <div style="background-color: #e8f5e9; padding: 12px; border-radius: 4px; margin: 16px 0;">
        <p style="font-size: 14px; margin: 4px 0;">• A TIL organiser will review your request</p>
        <p style="font-size: 14px; margin: 4px 0;">• If approved, ${data.totalHours} hours will be deducted from your balance</p>
        <p style="font-size: 14px; margin: 4px 0;">• You'll be notified by email when a decision is made</p>
      </div>
    </div>
  </div>
</body>
</html>
    `;
  }

  /**
   * Notify requester that their approved request has been cancelled by an organiser
   */
  async notifyRequesterRequestCancelled(params: {
    requesterId: string;
    requesterEmail: string;
    staffName: string;
    organiserName: string;
    requestId: string;
    requestType: 'EXTRA_HOURS' | 'TIL_REQUEST';
    requestDate: string;
    totalHours: number;
    previousBalance: number;
    balanceAdjustment: number;
    newBalance: number;
    cancellationReason?: string;
    notificationId?: string;
  }): Promise<{ success: boolean; error?: string }> {
    const viewRequestUrl = `${getBaseUrl()}/requests`;

    const html = this.renderRequestCancelled({
      staffName: params.staffName,
      organiserName: params.organiserName,
      requestType: params.requestType === 'EXTRA_HOURS' ? 'Extra Hours' : 'Time Off (TIL)',
      requestDate: params.requestDate,
      totalHours: params.totalHours,
      previousBalance: params.previousBalance,
      balanceAdjustment: params.balanceAdjustment,
      newBalance: params.newBalance,
      cancellationReason: params.cancellationReason,
      viewRequestUrl,
    });

    return this.sendEmail({
      to: params.requesterEmail,
      subject: 'Your TIL Request Has Been Cancelled',
      html,
      templateType: 'request_cancelled',
      requestId: params.requestId,
      notificationId: params.notificationId,
      userId: params.requesterId,
      metadata: { organiserName: params.organiserName, reason: params.cancellationReason },
    });
  }

  private renderRequestCancelled(data: any): string {
    const adjustmentText = data.balanceAdjustment > 0
      ? `+${data.balanceAdjustment} hours added back`
      : `${data.balanceAdjustment} hours removed`;

    return `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f6f9fc;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">
    <div style="padding: 32px 40px; background-color: #1976d2; color: #ffffff;">
      <h1 style="color: #ffffff; font-size: 28px; margin: 0 0 8px;">Request Cancelled</h1>
      <p style="color: #e3f2fd; font-size: 14px; margin: 0;">TIL Management System</p>
    </div>
    <div style="padding: 32px 40px;">
      <p>Hi <strong>${data.staffName}</strong>,</p>
      <div style="background-color: #fff3e0; border-left: 4px solid #ff9800; padding: 16px 20px; margin: 16px 0; text-align: center;">
        <p style="color: #e65100; font-size: 16px; margin: 0;">Your previously approved ${data.requestType} request has been <strong>cancelled</strong>.</p>
      </div>
      <div style="background-color: #f5f5f5; border-left: 4px solid #1976d2; border-radius: 4px; padding: 16px 20px; margin: 16px 0;">
        <p style="font-size: 14px; font-weight: bold; margin: 0 0 12px;">Request Details</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Type:</strong> ${data.requestType}</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Date:</strong> ${data.requestDate}</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Hours:</strong> ${data.totalHours} hours</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Cancelled by:</strong> ${data.organiserName}</p>
        ${data.cancellationReason ? `<p style="font-size: 14px; margin: 4px 0;"><strong>Reason:</strong> ${data.cancellationReason}</p>` : ''}
      </div>
      <div style="background-color: #e3f2fd; border-left: 4px solid #1976d2; border-radius: 4px; padding: 16px 20px; margin: 16px 0;">
        <p style="font-size: 14px; font-weight: bold; margin: 0 0 12px;">Balance Adjustment</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Previous Balance:</strong> ${data.previousBalance} hours</p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>Adjustment:</strong> <span style="color: #1976d2; font-weight: bold;">${adjustmentText}</span></p>
        <p style="font-size: 14px; margin: 4px 0;"><strong>New Balance:</strong> <span style="color: #1976d2; font-weight: bold;">${data.newBalance} hours</span></p>
      </div>
      <p>If you believe this was done in error, please contact the Daily Organiser.</p>
      <div style="text-align: center; margin: 24px 0;">
        <a href="${data.viewRequestUrl}" style="display: inline-block; background-color: #1976d2; color: #ffffff; font-size: 16px; font-weight: bold; text-decoration: none; padding: 12px 32px; border-radius: 4px;">View My Requests</a>
      </div>
    </div>
  </div>
</body>
</html>
    `;
  }
}

export default new SupabaseEmailService();
